import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from './ResetPassword.module.css';

export default function SetNewPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [validating, setValidating] = useState(true);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('No reset token provided.');
      setValidating(false);
      return;
    }

    // Validate the token
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`/api/auth/validate-reset-token/${token}`);
      const data = await response.json();

      if (data.valid) {
        setEmail(data.email);
        setTokenValid(true);
      } else {
        setError(data.error || 'Invalid or expired reset token.');
      }
    } catch (error) {
      console.error('Token validation error:', error);
      setError('An error occurred while validating your reset token.');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use the token to reset the password directly
      const resetResponse = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword: password,
        }),
      });

      const resetData = await resetResponse.json();

      if (!resetData.success) {
        setError(resetData.error || 'Failed to reset password.');
        return;
      }

      setSuccess('Password updated successfully! You can now log in with your new password.');

      // Redirect to login after showing success message
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error('Password reset error:', error);
      setError('An error occurred while resetting your password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className={styles.container}>
        <div className={styles.overlay} />
        <div className={styles.content}>
          <div className={styles.loadingSpinner}></div>
          <h1 className={styles.title}>Validating Reset Link...</h1>
          <p className={styles.message}>Please wait while we validate your password reset link.</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className={styles.container}>
        <div className={styles.overlay} />
        <div className={styles.content}>
          <h1 className={styles.title}>Invalid Reset Link</h1>
          <div className={styles.errorContainer}>
            <div className={styles.error}>{error}</div>
            <div className={styles.actions}>
              <button onClick={() => navigate('/forgot-password')} className={styles.button}>
                Request New Reset Link
              </button>
              <button onClick={() => navigate('/login')} className={styles.buttonSecondary}>
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.overlay} />
      <div className={styles.content}>
        <h1 className={styles.title}>Set New Password</h1>
        <p className={styles.message}>
          Enter your new password for <strong>{email}</strong>
        </p>

        {error && (
          <div className={styles.errorContainer}>
            <div className={styles.error}>{error}</div>
          </div>
        )}

        {success && (
          <div className={styles.successContainer}>
            <div className={styles.success}>{success}</div>
            <div className={styles.checkmark}>✓</div>
            <p className={styles.redirectMessage}>Redirecting you to login...</p>
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.inputGroup}>
              <label htmlFor="password" className={styles.label}>
                New Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className={styles.input}
                required
                minLength={6}
                placeholder="Enter your new password"
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="confirmPassword" className={styles.label}>
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className={styles.input}
                required
                minLength={6}
                placeholder="Confirm your new password"
              />
            </div>

            <button type="submit" disabled={loading} className={styles.button}>
              {loading ? 'Updating Password...' : 'Update Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
